import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { LOCAL_STORAGE_KEYS } from '../constants';

/**
 * This service acts as a middleware for router.
 * Responsible for
 *
 * 1) Guarding protected routes, which only authenticated users can access.
 * It uses the 'canActivate' property on routes. See routes.ts file
 *
 * 2) Authenticating jwt token
 *
 * 3) Guarding routes that the signed in user role is not allowed to open. A route
 * declares its own restriction through its data (see routes.ts):
 *    data: { allowedRoles: [2,5] } - only these roles may open the route
 *    data: { blockedRoles: [8] }   - every role except these may open the route
 * A route that declares neither stays open to every authenticated user, as before.
 * A blocked user is sent to the home page of his role - the driver (role 7) to the
 * driver plans, as on sign in, and every other role to the dashboard.
 * The role is taken from the user kept in the local storage on sign in, so the check
 * also works when the URL is typed directly, before the user API call returns.
 */
@Injectable()
export class AuthService {

	constructor(public router: Router) { }

	canActivate(route: ActivatedRouteSnapshot): boolean {
		if (!this.isTokenExists()) {
			this.router.navigate(['/login']);
			return false;
		}
		if (!this.isRouteAllowedForUserRole(route)) {
			this.router.navigate([this.getUserRoleId() == 7 ? '/driverplans' : '/dashboard']);
			return false;
		}
		return true;
	}

	isTokenExists = (): boolean => {
		return this.getToken() ? true : false;
	}

	getToken = () => {
		return localStorage.getItem(LOCAL_STORAGE_KEYS?.AUTH_TOKEN);
	}

	private isRouteAllowedForUserRole = (route: ActivatedRouteSnapshot): boolean => {
		const routeData = route ? route.data : null;
		const allowedRoles = routeData ? routeData['allowedRoles'] : null;
		const blockedRoles = routeData ? routeData['blockedRoles'] : null;
		if (allowedRoles == null && blockedRoles == null) {
			return true;
		}
		const userRoleId = this.getUserRoleId();
		if (allowedRoles != null) {
			return allowedRoles.some(roleId => roleId == userRoleId);
		}
		return !blockedRoles.some(roleId => roleId == userRoleId);
	}

	private getUserRoleId = () => {
		try {
			const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS?.USER));
			return user ? user['user_role_id'] : null;
		} catch (e) {
			return null;
		}
	}
}
